import React, { Fragment } from "react";
import { ActionType } from "../list/list/DataTable";
import { CashbackConfigV2, ListRequest, PlayerCashbackOverride, PlayerCashbackOverrideListResponse } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { EntityDataTable } from "../list/list/EntityDataTable";
import { PagerModel } from "../list/list/PagerModel";
import { ListUtils } from "../list/list/ListUtils";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { ListView, ListViewProps, ListViewState } from "../ListView";
import { Form, FormListener } from "../form/Form";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { Validators } from "../form/Validators";
import { Submit } from "../form/Submit";
import { SelectInput } from "../form/SelectInput";
import { AdminEntityService } from "../../services/AdminEntityService";
import { PlayerCashbackOverrideService, PlayerCashbackOverrideListRequest } from "../../services/PlayerCashbackOverrideService";
import { ColumnType } from "../list/list/ColumnType";
import { t } from "../../i18n";
import { FeatureFlags } from "../../feature-flags/FeatureFlagContext";
import { withFeatureFlag } from "../../feature-flags/withFeatureFlag";
import { formatExpiry, formatFraction } from "./utils/cashbackFormat";

interface State extends ListViewState<PlayerCashbackOverrideListRequest> {
  form?: PlayerCashbackOverrideListRequest;
  validationMap?: any;
  configs?: CashbackConfigV2[];
}

interface Props extends ListViewProps {
  cashbackV2Enabled: boolean;
}

export class PlayerCashbackOverrideListView
  extends ListView<State, PlayerCashbackOverrideListRequest, Props>
  implements FormListener<PlayerCashbackOverrideListRequest>
{
  // Column names are looked up as dataTable.header.{camelCase(name)}; pass the plain
  // header key so the shared table resolves a translated label like every other list view.
  columns = [
    {
      property: "userId",
      name: "User Id",
      type: ColumnType.LINK,
      getUrl: (item: PlayerCashbackOverride) => "/user/" + item.userId,
      headerStyle: { whiteSpace: "nowrap" as const },
    },
    {
      property: "configId",
      name: "Config",
      type: ColumnType.COMPONENT,
      component: (item: PlayerCashbackOverride) =>
        item.configId != null ? <Link to={"/cashback/v2/config/" + item.configId}>{this.configName(item.configId)}</Link> : <span />,
    },
    {
      property: "fraction",
      name: "Rate",
      type: ColumnType.COMPONENT,
      component: (item: PlayerCashbackOverride) => <span>{formatFraction(item.fraction)}</span>,
    },
    {
      property: "expiresAt",
      name: "Expires",
      // Sortability is driven by orderBy regardless of column type; the framework still
      // sorts this component column server-side by the expiresAt field.
      orderBy: "expiresAt",
      type: ColumnType.COMPONENT,
      component: (item: PlayerCashbackOverride) => (
        <span>{formatExpiry(item.expiresAt as unknown as number | undefined, t("cashback.player-overrides.no-expiry", undefined, "Permanent"))}</span>
      ),
    },
  ];

  actions = [
    {
      id: "edit",
      name: t("cashback.player-overrides.edit", undefined, "Edit"),
      className: "btn btn-primary",
      iconClassName: "fa fa-edit",
      type: ActionType.LINK,
      getLink: (item: PlayerCashbackOverride) => `/cashback/override/${item.userId}/${item.configId}`,
    },
    {
      id: "delete",
      name: t("cashback.player-overrides.remove", undefined, "Remove"),
      className: "btn btn-danger",
      iconClassName: "fa fa-trash",
      type: ActionType.CONFIRMATION,
    },
  ];

  service: PlayerCashbackOverrideService;
  configService = getContainer().getCashbackConfigV2Service();

  // EntityDataTable fetches through service.list(); adapt it to the query-string endpoint without touching the shared list framework.
  listService: AdminEntityService<PlayerCashbackOverride> = {
    list: (listRequest: ListRequest) => this.service.listPaged(listRequest as PlayerCashbackOverrideListRequest),
    save: () => Promise.reject(new Error("save is not supported on the player cashback override list")),
    getEntity: () => Promise.reject(new Error("getEntity is not supported on the player cashback override list")),
    remove: () => Promise.reject(new Error("remove is routed via onAction")),
  };

  constructor(props: Props) {
    super(props, "/cashback/player-overrides/list");
    this.service = getContainer().getPlayerCashbackOverrideService();
    const form: Partial<PlayerCashbackOverrideListRequest> = { ...this.state.listRequest };
    this.state = { ...this.state, validationMap: {}, configs: [], form };
    if (!this.state.listRequest?.orderBy) {
      this.state = { ...this.state, listRequest: { ...this.state.listRequest, orderBy: "id" } };
    }
  }

  loadContent(): Promise<any> {
    return this.configService.list({ limit: 1000, offset: 0, orderBy: "id" }).then((response) => this.setState({ configs: response.items ?? [] }));
  }

  // Configs load asynchronously into state, so fall back to the raw id until they arrive.
  configName(configId: number): string {
    const config = (this.state.configs ?? []).find((candidate) => candidate.id === configId);
    return config?.name ?? String(configId);
  }

  formDidUpdate(formModel: PlayerCashbackOverrideListRequest, validationMap?: any): void {
    this.setState({ form: formModel, validationMap: validationMap });
  }

  onSubmitError(): void {}

  onSubmit(): void | Promise<any> {
    if (this.state.form) {
      const form = { ...this.state.form, offset: 0 };
      this.updateListState(form);
    }
  }

  pagerChanged(pager: PagerModel) {
    if (this.state.listRequest) {
      const listRequest = ListUtils.toListRequest<PlayerCashbackOverrideListRequest>(this.state.listRequest, pager);
      this.updateListState(listRequest);
    }
  }

  onAction(item: PlayerCashbackOverride, action: string): Promise<any> | undefined {
    // Action id "delete" lets EntityDataTable strike the row optimistically once the promise resolves.
    if (action === "delete" && item.userId != null && item.configId != null) {
      return this.service.removeOverride(item.userId, item.configId);
    }
  }

  getTitle() {
    return t("cashback.player-overrides.title", undefined, "Player Cashback Overrides");
  }

  render() {
    // Gate the whole page (title chrome included) so a direct URL hit renders nothing when the
    // feature is off; the menu link is already hidden, this covers the directly-reachable route.
    if (!this.props.cashbackV2Enabled) {
      return <div />;
    }
    return super.render();
  }

  renderContent() {
    if (!this.state.listRequest) {
      return <div />;
    }
    return (
      <Fragment>
        <div className="wrapper wrapper-content">
          <div className="card-box">
            <h4>{t("cashback.player-overrides.filters", undefined, "Search Filters")}</h4>
            <Form
              formListener={this}
              modelObject={this.state.form}
              validationMap={this.state.validationMap}
            >
              <FormGroup className="form-group">
                <label>{t("cashback.player-overrides.user-id", undefined, "User ID")}</label>
                <FormInput
                  model="userId"
                  className="form-control"
                  type="integer"
                  validators={[Validators.integer()]}
                  data-testid="player-cashback-override-filter-user-id"
                />
              </FormGroup>
              <FormGroup className="form-group">
                <label>{t("cashback.player-overrides.config-id", undefined, "Config")}</label>
                <SelectInput
                  optional={true}
                  optionalText={t("cashback.player-overrides.all-configs", undefined, "All configs")}
                  model="configId"
                  className="form-control"
                  values={this.state.configs}
                  optionModelValue={(config: CashbackConfigV2) => config.id}
                  displayValue={(config: CashbackConfigV2) => config.name ?? String(config.id)}
                  data-testid="player-cashback-override-filter-config"
                />
              </FormGroup>
              <FormGroup className="form-group">
                <label className="d-block">
                  <FormInput
                    model="includeExpired"
                    type="checkbox"
                    className="mr-2"
                    data-testid="player-cashback-override-filter-include-expired"
                  />
                  {t("cashback.player-overrides.show-expired", undefined, "Show expired")}
                </label>
              </FormGroup>
              <Submit className="btn btn-primary">{t("cashback.player-overrides.search", undefined, "Search")}</Submit>
            </Form>
          </div>
          <div className="mb-3">
            <Link
              className="btn btn-primary"
              to="/cashback/override/new"
              data-testid="create-player-cashback-override"
            >
              {t("cashback.player-overrides.create", undefined, "Add Override")}
            </Link>
          </div>
          <EntityDataTable
            service={this.listService}
            onPagerChange={this.pagerChanged.bind(this)}
            columns={this.columns}
            listRequest={this.state.listRequest}
            onResults={(_response: PlayerCashbackOverrideListResponse) => undefined}
            onAction={this.onAction.bind(this)}
            actions={this.actions}
          />
        </div>
      </Fragment>
    );
  }
}

const withCashbackV2Enabled = withFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED, "cashbackV2Enabled");

export default withRouter(withCashbackV2Enabled(PlayerCashbackOverrideListView));
